/**
 * #651 observability — "dead click" diagnostics.
 *
 * Customers reported top-bar actions (campaigns) that silently do nothing, and we could
 * not reproduce. This reports ONE throttled `ui_action_stalled` event when either
 *   (a) a top-bar action's target surface has not mounted ~3s after the click, or
 *   (b) a page<->content `postMessageWithResponse` call times out,
 * carrying enough context to tell a dead bridge / orphaned content script from a
 * selector or render failure. Props are identifiers and numbers only — never message,
 * chat or contact content (cmd names and redux action TYPES are code constants).
 *
 * It also reports the exception behind a dead click: `render_error` (error boundary around
 * the campaigns surface), `handler_error` (a throw inside a top-bar click handler, with the
 * step), and on `mount_timeout` the last extension error seen within 5s before it. Errors
 * carry name, a short message and a trimmed stack only — never `cause` or custom fields.
 */
import { postMessageWithResponse } from './post-message-to-whatsapp-functions';
import type { OrphanCheckStep } from './orphan-guard';

export type UiStallReason = 'mount_timeout' | 'bridge_timeout' | 'render_error' | 'handler_error' | 'page_bridge_never_answered' | 'orphaned_context';
export type UiErrorReason = Extract<UiStallReason, 'render_error' | 'handler_error'>;
export type BridgePing = 'ok' | 'timeout' | 'error';

export interface UiStallProps {
  reason: UiStallReason;
  /** top-bar feature id (mount_timeout) or the timed-out bridge cmd (bridge_timeout). */
  action: string;
  extVersion: string | null;
  waVersion: string | null;
  bridgePing: BridgePing;
  bridgePingMs: number;
  runtimeIdPresent: boolean;
  msSincePageLoad: number;
  lastReduxAction: string | null;
  /** mount_timeout only */
  stateOpen?: boolean;
  containerPresent?: boolean;
  /** bridge_timeout only */
  timeoutMs?: number;
  /** render_error / handler_error only */
  step?: string;
  errorName?: string;
  errorMessage?: string;
  errorStack?: string;
  componentStack?: string;
  /** mount_timeout only: the last extension error seen within LAST_ERROR_WINDOW_MS before it */
  lastErrorName?: string;
  lastErrorMessage?: string;
  lastErrorStack?: string;
  lastErrorAgoMs?: number;
  /** mount_timeout only: size of the surface container (G3 — mounted but invisible). Absent when not measured. */
  containerVisible?: boolean;
  containerWidth?: number;
  containerHeight?: number;
  /** Last top-bar click seen (G2): which button and how long before this report. */
  lastClickAction?: string;
  lastClickAgoMs?: number;
  /** Reports lost since page load before this one got through (G4), and why the latest was lost. */
  reportsDropped?: number;
  lastDropReason?: DropReason;
}

/** Why a report never reached the backend. */
export type DropReason = 'throttled' | 'global_cap' | 'no_sink' | 'send_failed';

/** What a thrown value reduces to on the wire. */
export interface ErrorSummary {
  errorName: string;
  errorMessage: string;
  errorStack?: string;
}

/** The slice of `window` the global capture needs (injectable for tests). */
export interface ErrorCaptureTarget {
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
}

export type UiStallSink = (props: UiStallProps) => void;

export interface SurfaceProbe {
  stateOpen: boolean;
  containerPresent: boolean;
  /** Optional geometry (see readSurfaceGeometry). `containerVisible === false` means NOT mounted for the watch. */
  containerVisible?: boolean;
  containerWidth?: number;
  containerHeight?: number;
}

/** Viewport size used by the off-screen check (injectable for tests). */
export interface ViewportSize {
  width: number;
  height: number;
}

type RectEdges = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'>;

/** The part of a DOM Element the geometry probe reads. Element satisfies it structurally. */
export interface MeasurableSurface {
  getBoundingClientRect(): RectEdges & Pick<DOMRect, 'width' | 'height'>;
  checkVisibility?(): boolean;
}

function currentViewport(): ViewportSize | null {
  if (typeof window === 'undefined') return null;
  return { width: window.innerWidth, height: window.innerHeight };
}

/** True when the rect shares at least one pixel with the viewport. */
export function intersectsViewport(rect: RectEdges, viewport: ViewportSize): boolean {
  return rect.right > 0 && rect.bottom > 0 && rect.left < viewport.width && rect.top < viewport.height;
}

/**
 * Measure a surface container for the mount watch. A container that is in the DOM but has no
 * size, is hidden (display:none / visibility:hidden / content-visibility), or lies entirely
 * outside the viewport is not a surface the user can see — the "mounted but invisible" dead
 * click. #912: on WA 2.3000.1052525806 the campaigns overlay rendered one full viewport below
 * the screen (sized, checkVisibility() true), so the watch stayed silent. Never throws.
 */
export function readSurfaceGeometry(
  el: MeasurableSurface | null,
  viewport: ViewportSize | null = currentViewport(),
): Pick<SurfaceProbe, 'containerPresent' | 'containerVisible' | 'containerWidth' | 'containerHeight'> {
  if (!el) return { containerPresent: false };
  try {
    const rect = el.getBoundingClientRect();
    const containerWidth = Math.round(rect.width);
    const containerHeight = Math.round(rect.height);
    const shown = el.checkVisibility?.() ?? true;
    const onScreen = viewport === null || intersectsViewport(rect, viewport);
    return { containerPresent: true, containerVisible: containerWidth > 0 && containerHeight > 0 && shown && onScreen, containerWidth, containerHeight };
  } catch {
    return { containerPresent: true, containerVisible: false };
  }
}

export const MOUNT_WATCH_MS = 3000;
export const MOUNT_POLL_MS = 250;
const PING_TIMEOUT_MS = 2000;
const PING_SOURCE = 'ui-stall-ping';
const THROTTLE_PER_KEY_MS = 10 * 60 * 1000;
const GLOBAL_WINDOW_MS = 10 * 60 * 1000;
const GLOBAL_MAX_PER_WINDOW = 5;
export const LAST_ERROR_WINDOW_MS = 5000;
export const PAGE_BRIDGE_WATCH_MS = 30_000;
const MAX_ERROR_MESSAGE = 200;
const MAX_STACK = 2048;
const EXTENSION_URL_MARK = 'chrome-extension://';

let sink: UiStallSink | null = null;
let lastReduxAction: string | null = null;
let lastWaVersion: string | null = null;
const lastSentByKey = new Map<string, number>();
let recentSends: number[] = [];
let lastError: (ErrorSummary & { at: number }) | null = null;
let pageBridgeWatchArmed = false;
let lastClick: { action: string; at: number } | null = null;
let reportsDropped = 0;
let lastDropReason: DropReason | null = null;

export function setUiStallSink(next: UiStallSink | null): void {
  sink = next;
}

/** Called from the redux middleware on every action — type string only. */
export function noteReduxAction(type: string): void {
  lastReduxAction = type;
}

/** Called from a top-bar click handler: which button was clicked, so later reports can say so. */
export function noteUiClick(action: string): void {
  lastClick = { action, at: Date.now() };
}

function noteDrop(why: DropReason): void {
  reportsDropped += 1;
  lastDropReason = why;
}

/** Test hook. */
export function resetUiStallReporter(): void {
  sink = null;
  lastReduxAction = null;
  lastWaVersion = null;
  lastSentByKey.clear();
  recentSends = [];
  lastError = null;
  pageBridgeWatchArmed = false;
  lastClick = null;
  reportsDropped = 0;
  lastDropReason = null;
}

/** Reduce a thrown value to name + short message + trimmed stack. Nothing else is read. */
export function summarizeError(error: unknown): ErrorSummary {
  if (error instanceof Error) {
    return {
      errorName: String(error.name || 'Error').slice(0, 80),
      errorMessage: String(error.message ?? '').slice(0, MAX_ERROR_MESSAGE),
      ...(typeof error.stack === 'string' ? { errorStack: error.stack.slice(0, MAX_STACK) } : {}),
    };
  }
  let message: string;
  try {
    message = String(error);
  } catch {
    message = 'unprintable';
  }
  return { errorName: 'NonError', errorMessage: message.slice(0, MAX_ERROR_MESSAGE) };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Keep only errors raised by extension code, so WhatsApp's own page errors never become lastError. */
function noteGlobalError(error: unknown, filename: unknown): void {
  if (!(error instanceof Error)) return;
  const fromExtension = (typeof filename === 'string' && filename.startsWith(EXTENSION_URL_MARK))
    || (typeof error.stack === 'string' && error.stack.includes(EXTENSION_URL_MARK));
  if (!fromExtension) return;
  lastError = { ...summarizeError(error), at: Date.now() };
}

/**
 * Record uncaught errors and unhandled rejections from extension code into one slot that the
 * next mount_timeout reads. Capture never sends anything itself (sends are throttled in
 * `report`), so overwriting the slot is the whole rate limit. Returns an uninstall function.
 */
export function installGlobalErrorCapture(target: ErrorCaptureTarget): () => void {
  const onError = (event: unknown) => {
    if (isRecord(event)) noteGlobalError(event.error, event.filename);
  };
  const onRejection = (event: unknown) => {
    if (isRecord(event)) noteGlobalError(event.reason, undefined);
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}

function recentLastError(now: number): Detail {
  if (!lastError || now - lastError.at > LAST_ERROR_WINDOW_MS) return {};
  return {
    lastErrorName: lastError.errorName,
    lastErrorMessage: lastError.errorMessage,
    ...(lastError.errorStack !== undefined ? { lastErrorStack: lastError.errorStack } : {}),
    lastErrorAgoMs: now - lastError.at,
  };
}

function readExtVersion(): string | null {
  try {
    return chrome.runtime.getManifest().version ?? null;
  } catch {
    return null; // orphaned context throws "Extension context invalidated"
  }
}

function readRuntimeIdPresent(): boolean {
  try {
    return typeof chrome !== 'undefined' && !!chrome.runtime?.id;
  } catch {
    return false;
  }
}

async function pingBridge(): Promise<{ result: BridgePing; ms: number }> {
  const t0 = performance.now();
  try {
    const res = await postMessageWithResponse(
      { cmd: 'getWaVersion', timeoutOverride: PING_TIMEOUT_MS },
      PING_SOURCE,
    );
    if (typeof res === 'string') lastWaVersion = res;
    return { result: 'ok', ms: Math.round(performance.now() - t0) };
  } catch (e) {
    const timedOut = e instanceof Error && /Timeout after/.test(e.message);
    return { result: timedOut ? 'timeout' : 'error', ms: Math.round(performance.now() - t0) };
  }
}

function allowSend(key: string, now: number): boolean {
  const last = lastSentByKey.get(key);
  if (last !== undefined && now - last < THROTTLE_PER_KEY_MS) {
    noteDrop('throttled');
    return false;
  }
  recentSends = recentSends.filter((t) => now - t < GLOBAL_WINDOW_MS);
  if (recentSends.length >= GLOBAL_MAX_PER_WINDOW) {
    noteDrop('global_cap');
    return false;
  }
  lastSentByKey.set(key, now);
  recentSends.push(now);
  return true;
}

type Detail = Partial<Pick<UiStallProps,
  | 'stateOpen' | 'containerPresent' | 'containerVisible' | 'containerWidth' | 'containerHeight' | 'timeoutMs'
  | 'step' | 'errorName' | 'errorMessage' | 'errorStack' | 'componentStack'
  | 'lastErrorName' | 'lastErrorMessage' | 'lastErrorStack' | 'lastErrorAgoMs'>>;

interface ReportOptions {
  throttleKey?: string;
  /** Drop the report when the bridge ping is answered (the condition cleared on its own). */
  skipIfPingOk?: boolean;
}

async function report(reason: UiStallReason, action: string, detail: Detail, options: ReportOptions = {}): Promise<void> {
  if (!sink) {
    noteDrop('no_sink');
    return;
  }
  const now = Date.now();
  if (!allowSend(options.throttleKey ?? `${reason}:${action}`, now)) return;
  // Read before the ping: the ping can take up to 2s and would shift the 5s window.
  if (reason === 'mount_timeout') detail = { ...detail, ...recentLastError(now) };
  try {
    const ping = await pingBridge();
    if (options.skipIfPingOk && ping.result === 'ok') return;
    sink({
      reason,
      action,
      extVersion: readExtVersion(),
      waVersion: lastWaVersion,
      bridgePing: ping.result,
      bridgePingMs: ping.ms,
      runtimeIdPresent: readRuntimeIdPresent(),
      msSincePageLoad: Math.round(performance.now()),
      lastReduxAction,
      ...(lastClick ? { lastClickAction: lastClick.action, lastClickAgoMs: Date.now() - lastClick.at } : {}),
      ...(reportsDropped > 0 && lastDropReason ? { reportsDropped, lastDropReason } : {}),
      ...detail,
    });
  } catch (e) {
    noteDrop('send_failed');
    console.debug('[ui-stall-reporter] report failed', e);
  }
}

/**
 * Report the exception behind a dead click. `step` names the handler step that threw
 * (handler_error); `componentStack` comes from an error boundary (render_error). Throttled per
 * action + step. Never throws.
 */
export function reportUiError(
  reason: UiErrorReason,
  action: string,
  error: unknown,
  extra: { step?: string; componentStack?: string } = {},
): void {
  try {
    const detail: Detail = {
      ...summarizeError(error),
      ...(extra.step !== undefined ? { step: extra.step } : {}),
      ...(extra.componentStack !== undefined ? { componentStack: extra.componentStack.slice(0, MAX_STACK) } : {}),
    };
    void report(reason, action, detail, { throttleKey: `${reason}:${action}:${extra.step ?? ''}` });
  } catch (e) {
    console.debug('[ui-stall-reporter] reportUiError failed', e);
  }
}

/**
 * #764 fix 4 stopped reporting bridge timeouts until the page bridge has answered once, which
 * made a WhatsApp tab whose page bridge NEVER comes up silent. This restores that signal: once
 * per tab, `delayMs` after it is armed, if the bridge still has not answered, ping it — and
 * report `page_bridge_never_answered` only if the ping fails too (no calls yet ≠ dead bridge).
 * Arm it on the WhatsApp page only.
 */
export function watchPageBridgeAnswered(isAnswered: () => boolean, delayMs: number = PAGE_BRIDGE_WATCH_MS): void {
  if (pageBridgeWatchArmed) return;
  pageBridgeWatchArmed = true;
  const timer = setTimeout(() => {
    let answered = false;
    try {
      answered = isAnswered();
    } catch {
      answered = false;
    }
    if (!answered) void report('page_bridge_never_answered', 'pageBridge', { timeoutMs: delayMs }, { skipIfPingOk: true });
  }, delayMs);
  (timer as unknown as { unref?: () => void })?.unref?.();
}

/**
 * The orphan guard found this content script orphaned (`step` = the check that found it). One
 * event per tab. With no sink the report is dropped (FE#788's drop counter covers it once landed).
 */
export function reportOrphanedContext(step: OrphanCheckStep): void {
  void report('orphaned_context', 'orphanGuard', { step }, { throttleKey: 'orphaned_context' });
}

/** postMessageWithResponse timeout hook. Ignores our own ping so it cannot recurse. */
export function reportBridgeTimeout(cmd: string, source: string, timeoutMs: number): void {
  if (source === PING_SOURCE) return;
  void report('bridge_timeout', cmd, { timeoutMs });
}

/**
 * Call right after a top-bar click. Polls `probe()` every `MOUNT_POLL_MS`; once the surface
 * has been seen mounted the watch ends silently. Reports only if it was NEVER seen mounted
 * by `delayMs`. A single check at the deadline misread "opened, then the user closed it
 * within 3s" as a dead click (stateOpen=false, containerPresent=false). Returns a cancel
 * function.
 */
export function watchSurfaceMount(
  action: string,
  probe: () => SurfaceProbe,
  delayMs: number = MOUNT_WATCH_MS,
): () => void {
  const read = (): SurfaceProbe => {
    try {
      return probe();
    } catch {
      return { stateOpen: false, containerPresent: false };
    }
  };
  const startedAt = Date.now();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const tick = () => {
    const state = read();
    if (state.stateOpen && state.containerPresent && state.containerVisible !== false) return;
    const elapsed = Date.now() - startedAt;
    if (elapsed >= delayMs) {
      void report('mount_timeout', action, state);
      return;
    }
    timer = setTimeout(tick, Math.min(MOUNT_POLL_MS, delayMs - elapsed));
  };
  timer = setTimeout(tick, Math.min(MOUNT_POLL_MS, delayMs));
  return () => {
    if (timer !== null) clearTimeout(timer);
  };
}
